/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import dayjs from 'dayjs'
// img
import { ArrowIco } from '@shared/Icon/ArrowIcon'
// css
import { SelectMonth } from '@styles/button.style'
import { AdvButton } from '@components/Button'
import { ListView } from 'antd-mobile'
import { isMobile } from 'react-device-detect'
import { ActivityHistoryItem } from '@views/MyPage/UserActivity/ActivityHistory/ActivityHistoryItem'
import { ActivityHistoryWrap } from '@views/MyBank/index.style'
import { TxtLimit } from '@styles/base.style'
import useRootStore from '@store/useRootStore'
import { Schema$UserActivityTrack } from '@interface/user-activity'
import { useOnUnmount } from '@utils/reactExt'
import _ from 'lodash'
import { AdvEmpty } from '@components/AdvEmpty'
import { EMPTY_ICON } from '@constants/index'
import classNames from 'classnames'
import { isProdEnv, isQaEnv, isTestEnv } from '@utils/utils'

const PAGE_SIZE = 30

const dataSource = new ListView.DataSource({
  rowHasChanged: (row1, row2) => row1 !== row2,
})

export const ActivityHistory: React.FC = observer(() => {
  const { userStore } = useRootStore()
  const { myActivityHistoryTracks, myActivityHistoryHasMore, myActivityHistoryScore } = userStore
  const [month, setMonth] = useState<string>(dayjs().format('YYYY-MM'))
  const [pageNum, setPageNum] = useState<number>(0)

  const fetch = () => {
    userStore.fetchMyActivityHistory(month, pageNum, PAGE_SIZE).catch(console.error)
  }

  useEffect(() => {
    fetch()
  }, [pageNum, month])

  useOnUnmount(() => {
    userStore.resetMyActivityHistory()
  })

  const isPreviousButtonDisabled = () => {
    let activityStartMonth = dayjs('2023-07-01')
    if (isTestEnv()) {
      activityStartMonth = dayjs('2023-07-01')
    } else if (isQaEnv() || isProdEnv()) {
      activityStartMonth = dayjs('2023-08-01')
    }
    const oneYearAgo = dayjs().add(-11, 'months').startOf('month')
    return dayjs(month).add(-1, 'months').isBefore(activityStartMonth)
      || dayjs(month).isBefore(oneYearAgo)
  }

  const isNextButtonDisabled = () => dayjs().startOf('months').isSame(dayjs(month))

  const onClickPreviousButton = () => {
    if (isPreviousButtonDisabled()) {
      return
    }

    const newMonth = dayjs(month).add(-1, 'months').format('YYYY-MM')
    setMonth(newMonth)
    setPageNum(0)
  }

  const onClickNextButton = () => {
    if (isNextButtonDisabled()) {
      return
    }

    const newMonth = dayjs(month).add(1, 'months').format('YYYY-MM')
    setMonth(newMonth)
    setPageNum(0)
  }

  const renderTitle = (): React.ReactElement => {
    return (
      <Fragment>
        <SelectMonth className="activity-score-record">
          <AdvButton
            width={40}
            height={40}
            ico={<ArrowIco/>}
            icoColor="darkTxt"
            icoRotate={90}
            icoSize={12}
            className={`prev-btn activity-reward-list ${isPreviousButtonDisabled() ? 'disabled' : ''}`}
            onClick={onClickPreviousButton}
          />
          <div className="date">
            <span className="year">{dayjs(month).format('YYYY')}</span>
            <span className="month">{dayjs(month).format('M')}월</span>
          </div>
          <AdvButton
            width={40}
            height={40}
            ico={<ArrowIco/>}
            icoColor="darkTxt"
            icoRotate={-90}
            icoSize={12}
            className={`next-btn activity-reward-list ${isNextButtonDisabled() ? 'disabled' : ''}`}
            onClick={onClickNextButton}
          />
        </SelectMonth>
        <div className="money-row">
          <span className="money-row--txt">활동점수</span>
          <span className="money-row--money-wrap">
              <span className="money-row--money-wrap--num" css={TxtLimit}>{myActivityHistoryScore}점</span>
          </span>
        </div>
      </Fragment>
    )
  }

  const endReachedHandler = () => {
    if (myActivityHistoryHasMore) {
      setPageNum(pageNum + 1)
    }
  }

  const renderRow = (item: Schema$UserActivityTrack) => {
    return <ActivityHistoryItem track={item} />
  }

  const renderFooter = () => {
    let footer
    if (_.size(myActivityHistoryTracks) > 0) {
      footer = <Fragment/>
    } else {
      footer = <AdvEmpty ico={EMPTY_ICON.ACTIVITY_HISTORY_LIST}/>
    }
    return <div css={{ textAlign: 'center' }}>{footer}</div>
  }

  const wrapClassNames = classNames('activity-history-popup', {
    'pc': !isMobile,
  })
  const listViewClassNames = classNames('activity-history-list popup', {
    'pc': !isMobile
  })

  return (
    <ActivityHistoryWrap className={wrapClassNames}>
      <ListView
        dataSource={dataSource.cloneWithRows(myActivityHistoryTracks)}
        renderHeader={renderTitle}
        renderRow={renderRow}
        renderFooter={renderFooter}
        scrollRenderAheadDistance={500}
        onEndReachedThreshold={10}
        pageSize={PAGE_SIZE}
        initialListSize={PAGE_SIZE}
        onEndReached={endReachedHandler}
        className={listViewClassNames}
      />
    </ActivityHistoryWrap>
  )
})
